// CommandBar.tsx - Global Command Palette (Cmd+K)
// Provides quick access to navigation, search, and actions across the CRM

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { supabase } from "../providers/supabase/supabase";
import {
    Building2,
    Home,
    Megaphone,
    Plus,
    Search,
    Settings,
    User,
    Users,
    Mail,
    Briefcase,
} from "lucide-react";

import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
    CommandShortcut,
} from "@/components/ui/command";

import { useCampaignOptional } from "../context/CampaignContext";
import { useOrganization } from "../context/OrganizationContext";

interface SearchResult {
    id: number | string;
    type: "contact" | "company" | "deal";
    title: string;
    subtitle?: string;
}

export function CommandBar() {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    const navigate = useNavigate();
    const campaignContext = useCampaignOptional();
    const { currentOrg, currentOrgId } = useOrganization();

    // Keyboard shortcut listener
    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((open) => !open);
            }
        };

        document.addEventListener("keydown", down);
        return () => document.removeEventListener("keydown", down);
    }, []);

    // Search across contacts, companies, and deals using direct supabase queries
    // This bypasses campaign filtering so we can search across ALL campaigns in the org
    const performSearch = useCallback(
        async (query: string) => {
            if (query.length < 2 || !currentOrgId) {
                setSearchResults([]);
                return;
            }

            setIsSearching(true);
            const results: SearchResult[] = [];
            const searchPattern = `%${query}%`;

            // Search contacts (across all campaigns in this org)
            try {
                const { data: contacts, error } = await supabase
                    .from("contacts")
                    .select("id, first_name, last_name, email_jsonb, campaign_id")
                    .eq("org_id", currentOrgId)
                    .or(`first_name.ilike.${searchPattern},last_name.ilike.${searchPattern}`)
                    .order("last_seen", { ascending: false })
                    .limit(5);

                if (!error && contacts) {
                    contacts.forEach((contact: any) => {
                        const name = `${contact.first_name || ""} ${contact.last_name || ""}`.trim();
                        const email = contact.email_jsonb?.[0]?.email;
                        results.push({
                            id: contact.id,
                            type: "contact",
                            title: name || "Unknown Contact",
                            subtitle: email,
                        });
                    });
                }
            } catch (error) {
                console.warn("Contact search failed:", error);
            }

            // Search companies
            try {
                const { data: companies, error } = await supabase
                    .from("companies")
                    .select("id, name, sector")
                    .eq("org_id", currentOrgId)
                    .ilike("name", searchPattern)
                    .order("name", { ascending: true })
                    .limit(5);

                if (!error && companies) {
                    companies.forEach((company: any) => {
                        results.push({
                            id: company.id,
                            type: "company",
                            title: company.name || "Unknown Company",
                            subtitle: company.sector,
                        });
                    });
                }
            } catch (error) {
                console.warn("Company search failed:", error);
            }

            // Search deals
            try {
                const { data: deals, error } = await supabase
                    .from("deals")
                    .select("id, name, amount, campaign_id")
                    .eq("org_id", currentOrgId)
                    .ilike("name", searchPattern)
                    .order("updated_at", { ascending: false })
                    .limit(5);

                if (!error && deals) {
                    deals.forEach((deal: any) => {
                        results.push({
                            id: deal.id,
                            type: "deal",
                            title: deal.name || "Untitled Deal",
                            subtitle: deal.amount ? `$${deal.amount.toLocaleString()}` : undefined,
                        });
                    });
                }
            } catch (error) {
                console.warn("Deal search failed:", error);
            }

            setSearchResults(results);
            setIsSearching(false);
        },
        [currentOrgId]
    );

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            performSearch(search);
        }, 300);

        return () => clearTimeout(timer);
    }, [search, performSearch]);

    const handleSelect = (callback: () => void) => {
        setOpen(false);
        setSearch("");
        setSearchResults([]);
        callback();
    };

    const runCommand = useCallback(
        (command: () => void) => {
            handleSelect(command);
        },
        []
    );

    return (
        <>
            {/* Trigger button - subtle hint in the header */}
            <button
                onClick={() => setOpen(true)}
                className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground bg-muted/50 hover:bg-muted rounded-md border border-input transition-colors"
            >
                <Search className="h-4 w-4" />
                <span className="hidden lg:inline">Search...</span>
                <kbd className="pointer-events-none hidden lg:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                    <span className="text-xs">⌘</span>K
                </kbd>
            </button>

            <CommandDialog
                open={open}
                onOpenChange={setOpen}
                title="Command Palette"
                description="Search contacts, companies, deals, or run quick commands"
            >
                <CommandInput
                    placeholder="Type to search or run a command..."
                    value={search}
                    onValueChange={setSearch}
                />
                <CommandList>
                    <CommandEmpty>
                        {isSearching ? "Searching..." : "No results found."}
                    </CommandEmpty>

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                        <CommandGroup heading="Search Results">
                            {searchResults.map((result) => (
                                <CommandItem
                                    key={`${result.type}-${result.id}`}
                                    value={`${result.type}-${result.id}-${result.title}`}
                                    onSelect={() =>
                                        runCommand(() =>
                                            navigate(`/${result.type === "contact" ? "contacts" : result.type === "company" ? "companies" : "deals"}/${result.id}/show`)
                                        )
                                    }
                                >
                                    {result.type === "contact" && <User className="mr-2 h-4 w-4 text-blue-500" />}
                                    {result.type === "company" && <Building2 className="mr-2 h-4 w-4 text-green-500" />}
                                    {result.type === "deal" && <Briefcase className="mr-2 h-4 w-4 text-purple-500" />}
                                    <div className="flex flex-col">
                                        <span>{result.title}</span>
                                        {result.subtitle && (
                                            <span className="text-xs text-muted-foreground">{result.subtitle}</span>
                                        )}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    )}

                    {/* Quick Navigation */}
                    {search.length < 2 && (
                        <>
                            <CommandGroup heading="Navigation">
                                <CommandItem
                                    value="dashboard"
                                    onSelect={() => runCommand(() => navigate("/"))}
                                >
                                    <Home className="mr-2 h-4 w-4" />
                                    <span>Dashboard</span>
                                    <CommandShortcut>⌘D</CommandShortcut>
                                </CommandItem>
                                <CommandItem
                                    value="contacts"
                                    onSelect={() => runCommand(() => navigate("/contacts"))}
                                >
                                    <Users className="mr-2 h-4 w-4" />
                                    <span>Contacts</span>
                                </CommandItem>
                                <CommandItem
                                    value="companies"
                                    onSelect={() => runCommand(() => navigate("/companies"))}
                                >
                                    <Building2 className="mr-2 h-4 w-4" />
                                    <span>Companies</span>
                                </CommandItem>
                                <CommandItem
                                    value="deals"
                                    onSelect={() => runCommand(() => navigate("/deals"))}
                                >
                                    <Briefcase className="mr-2 h-4 w-4" />
                                    <span>Deals</span>
                                </CommandItem>
                            </CommandGroup>

                            <CommandSeparator />

                            <CommandGroup heading="Quick Actions">
                                <CommandItem
                                    value="new-contact"
                                    onSelect={() => runCommand(() => navigate("/contacts/create"))}
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    <span>New Contact</span>
                                    <CommandShortcut>⌘N</CommandShortcut>
                                </CommandItem>
                                <CommandItem
                                    value="new-company"
                                    onSelect={() => runCommand(() => navigate("/companies/create"))}
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    <span>New Company</span>
                                </CommandItem>
                                <CommandItem
                                    value="new-deal"
                                    onSelect={() => runCommand(() => navigate("/deals/create"))}
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    <span>New Deal</span>
                                </CommandItem>
                            </CommandGroup>

                            <CommandSeparator />

                            <CommandGroup heading="Settings">
                                <CommandItem
                                    value="my-settings"
                                    onSelect={() => runCommand(() => navigate("/settings"))}
                                >
                                    <Settings className="mr-2 h-4 w-4" />
                                    <span>My Info</span>
                                </CommandItem>
                                <CommandItem
                                    value="email-settings"
                                    onSelect={() => runCommand(() => navigate("/settings/email"))}
                                >
                                    <Mail className="mr-2 h-4 w-4" />
                                    <span>Email Settings</span>
                                </CommandItem>
                            </CommandGroup>

                            {/* Campaign Info */}
                            {campaignContext?.currentCampaign && (
                                <>
                                    <CommandSeparator />
                                    <CommandGroup heading="Current Context">
                                        <CommandItem disabled>
                                            <Megaphone className="mr-2 h-4 w-4 text-primary" />
                                            <span>
                                                Campaign: {campaignContext.currentCampaign.name}
                                            </span>
                                            <span className="ml-2 text-xs text-muted-foreground">
                                                ({campaignContext.currentCampaign.contact_count} contacts)
                                            </span>
                                        </CommandItem>
                                        {currentOrg && (
                                            <CommandItem disabled>
                                                <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
                                                <span className="text-muted-foreground">
                                                    Org: {currentOrg.name}
                                                </span>
                                            </CommandItem>
                                        )}
                                    </CommandGroup>
                                </>
                            )}
                        </>
                    )}
                </CommandList>
            </CommandDialog>
        </>
    );
}

export default CommandBar;
